import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Hexagon, Printer, ArrowLeft, Phone, Mail, Shield } from "lucide-react";
import { PricingGrid } from "@/components/pricing/PricingContent";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

// ---------------------------------------------------------------------------
// Leave-Behind — single LANDSCAPE printable that mirrors the public /pricing
// page exactly (dark theme, full 4-card grid, footer trio).
//
// • On screen: identical to /pricing.
// • On print: landscape Letter, dark theme preserved (Chrome/Edge "Background
//   graphics" must be ON — most browsers turn this on by default for
//   print-to-PDF; we also force `-webkit-print-color-adjust: exact`).
// ---------------------------------------------------------------------------

export default function LeaveBehindPage() {
  useDocumentTitle("OmniDraftLab · Leave-Behind");

  // Hide platform "Made with Emergent" badge on this page only.
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "leavebehind-hide-emergent";
    style.textContent = `[data-testid="made-with-emergent-badge"], #emergent-watermark { display: none !important; }`;
    document.head.appendChild(style);
    return () => { document.getElementById("leavebehind-hide-emergent")?.remove(); };
  }, []);

  return (
    <div
      className="min-h-screen text-1"
      style={{
        background:
          "radial-gradient(ellipse 80% 60% at 20% 10%, rgba(34,211,238,0.08), transparent 60%), radial-gradient(ellipse 70% 50% at 85% 85%, rgba(251,113,133,0.06), transparent 60%), #04111A",
      }}
      data-testid="leavebehind-page"
    >
      {/* Top toolbar — preview only, hidden on print */}
      <div className="lb-no-print sticky top-0 z-40 backdrop-blur-md bg-[#04111A]/70 border-b border-white/5">
        <div className="max-w-[1500px] mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/pitch" className="inline-flex items-center gap-2 text-2 hover:text-1 text-sm" data-testid="leavebehind-back">
            <ArrowLeft className="h-4 w-4" /> Back to pitch
          </Link>
          <div className="flex items-center gap-2 text-3 text-xs uppercase tracking-[0.22em]">
            <Hexagon className="h-3.5 w-3.5 text-cyan-300" /> Leave-Behind · Landscape · 1 page
          </div>
          <button
            data-testid="leavebehind-print"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-300 text-[#04111A] font-semibold text-sm hover:bg-cyan-200 transition"
          >
            <Printer className="h-4 w-4" /> Print / Save PDF
          </button>
        </div>
      </div>

      {/* Main — vertically centered on screen, sized for landscape on print */}
      <div className="lb-stage max-w-[1500px] mx-auto px-6 py-10 sm:py-14 min-h-[calc(100vh-64px)] flex flex-col justify-center">
        {/* Eyebrow + headline */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-cyan-300 mb-4">
            <span className="h-px w-10 bg-cyan-300/60" /> Engagement Tiers
          </div>
          <h1
            className="font-semibold tracking-tight leading-[1.05] text-1"
            style={{ fontSize: "clamp(2rem, 4.4vw, 3.6rem)" }}
          >
            Built for <span className="text-cyan-300 italic font-light">serious</span> prospects only.
          </h1>
          <p className="mt-5 text-2 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            Four engagement depths. Every tier is application-only — a limited number of clients per draft class so every athlete gets the full attention of the advisory team.
          </p>
        </div>

        {/* The 4 cards */}
        <div className="mt-12 lb-grid">
          <PricingGrid />
        </div>

        {/* Footer block — trust + contact */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto w-full">
          <FooterCard
            icon={Shield}
            title="What every tier includes"
            body="Direct access to a former NFL personnel executive. Written reports, protected deliverables, and a one-pager summary after every major deliverable."
          />
          <FooterCard
            icon={Phone}
            title="How to apply"
            body="15-minute intro call with the founder. Bring your timeline, your athlete's tape, and your questions. We'll return a tier recommendation and a written proposal within 72 hours."
          />
          <FooterCard
            icon={Mail}
            title="Contact"
            body={<>
              <a className="text-cyan-300 hover:text-cyan-200" href="mailto:hello@omnidraftlab.com">hello@omnidraftlab.com</a>
              <div className="mt-1 text-3 text-xs">Applications reviewed weekly · replies within 48 hrs.</div>
            </>}
          />
        </div>

        {/* Fine print */}
        <div className="mt-10 text-center text-3 text-[11px] leading-relaxed max-w-3xl mx-auto italic">
          All engagements require an executed advisory agreement. Pricing reflects complexity of the prospect, timeline, and scope of deliverables. Outcomes belong to teams; readiness — across all four layers — belongs to us.
        </div>

        {/* Footer bar */}
        <div className="mt-8 text-center text-3 text-[10px] uppercase tracking-[0.22em]">
          OmniDraftLab · Draft Class {new Date().getFullYear() + 1} · omnidraftlab.com
        </div>
      </div>

      {/* PRINT STYLES — landscape Letter, dark theme preserved exactly */}
      <style>{`
        @page { size: letter landscape; margin: 0.35in; }

        @media print {
          html, body {
            background: #04111A !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          [data-testid="leavebehind-page"] {
            background: #04111A !important;
            background-image:
              radial-gradient(ellipse 80% 60% at 20% 10%, rgba(34,211,238,0.08), transparent 60%),
              radial-gradient(ellipse 70% 50% at 85% 85%, rgba(251,113,133,0.06), transparent 60%) !important;
            min-height: 0 !important;
          }
          /* Hide screen toolbar & sticky positioning */
          .lb-no-print { display: none !important; }
          .lb-stage {
            min-height: 0 !important;
            padding: 0 !important;
            max-width: 100% !important;
            display: block !important;
          }
          /* Tighten vertical rhythm so it fits on one landscape sheet */
          .lb-stage > *:first-child { margin-top: 0 !important; }
          .lb-grid { margin-top: 1rem !important; }
          .lb-stage h1 { font-size: 2.1rem !important; line-height: 1.05 !important; }
          .lb-stage > .text-center.max-w-3xl p { font-size: 0.85rem !important; margin-top: 0.6rem !important; }
          .lb-stage .grid.grid-cols-1.md\\:grid-cols-3 { margin-top: 1rem !important; }

          /* Compact pricing cards on print */
          [data-testid^="pricing-card-"] {
            padding: 0.9rem 1rem !important;
            border-radius: 12px !important;
          }
          [data-testid^="pricing-card-"] h3 { font-size: 0.95rem !important; line-height: 1.25 !important; margin-top: 0.6rem !important; }
          [data-testid^="pricing-card-"] p  { font-size: 0.72rem !important; line-height: 1.35 !important; margin-top: 0.4rem !important; }
          [data-testid^="pricing-card-"] li { font-size: 0.72rem !important; line-height: 1.3 !important; }
          [data-testid^="pricing-card-"] ul { gap: 0.25rem !important; }
          [data-testid^="pricing-card-"] ul > li + li { margin-top: 0.25rem !important; }
          /* Hide "Apply with this tier" CTA on print — leave-behind is read, not clicked */
          [data-testid^="pricing-apply-"] { display: none !important; }

          /* Force the pricing grid to stay 4-column on landscape print */
          .lb-grid > .grid {
            grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
            gap: 0.6rem !important;
          }

          /* Ensure single page */
          .lb-stage, .lb-stage > * { page-break-inside: avoid; break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}

function FooterCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-5 text-left">
      <div className="flex items-center gap-2 text-cyan-300">
        <Icon className="h-3.5 w-3.5" />
        <div className="text-[10px] uppercase tracking-[0.22em] font-medium">{title}</div>
      </div>
      <div className="mt-2 text-2 text-sm leading-relaxed">{body}</div>
    </div>
  );
}
